import { useGetAiWorkerUsageByIdMutation, useTypedSelector, useGetAgentHisotryMutation, RootState, useGetAiWorkersByCategoryQuery, categoryType, useGetMappersListQuery } from "@circuitry-ai/doc-data";
import { useTranslation } from "react-i18next";
import { formatDateTime12Hour, CtyShowToast, checkType, agGridToApiCondition, appendAgGridConditionsToApiFilters, parseScore, useAgGridStatePersistence, getPersistedTableQueryState, persistedSortForApi, CtyButton, CtySpinner, UsageQueryExportButton } from "@circuitry-ai/doc-ui";
import { useState, useEffect, useRef, startTransition, useCallback, useMemo } from "react";
import AgentExecutionDataRenderer from "./AgentExecutionDataRenderer";
import { AgentExecutePanel } from "./AgentExecutePanel";
import AiworkersUsageEventModal from "./AiworkersUsageEventModal";
import { CallLogDrawer } from '@circuitry-ai/doc-voice-agent';
import { Icon } from '@iconify/react';
import { Select, SelectItem, Tooltip } from "@nextui-org/react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { DateFilterComponent, CustomDateFilter, DebouncedTextFloatingFilter } from '@circuitry-ai/doc-ui';
import { ErrorBoundAgGridTable } from './AgentTableCommon';

const AGENT_HISTORY_TABLE_ID = 'agent-history-table';
const DEFAULT_PAGE_SIZE = 20;

const countColumnCellStyle = { alignItems: 'center' as const, display: 'flex' as const, justifyContent: 'center' as const };

function buildAgentHistoryQueryPayload({
    page,
    perPage,
    columnFilters,
    selectedAgentFilter,
    sort,
}: {
    page: number;
    perPage: number;
    columnFilters: any[];
    selectedAgentFilter: string;
    sort?: { field: string; order: string };
}) {
    const filters: any[] = [...columnFilters, { aiworker_type: 'action_logs', condition: 'contains' }];
    if (selectedAgentFilter && selectedAgentFilter !== 'all') {
        filters.push({ aiworker_id: selectedAgentFilter, condition: 'contains' });
    }
    return {
        pagination: { page, per_page: perPage },
        filters,
        ...(sort?.field && sort?.order ? { sorting: { field: sort.field, order: sort.order } } : {}),
    };
}

function buildAgentHistoryFilters(filterModel: Record<string, any> | null | undefined): any[] {
    if (!filterModel) return [];
    const filters: any[] = [];
    for (const [key, rawValue] of Object.entries(filterModel)) {
        const value: any = rawValue;
        if (value?.filterType === 'date') {
            const type = value.type;
            const from = value.dateFrom || value.filter ? (value.dateFrom || value.filter).split(' ')[0] : null;
            const to = value.dateTo ? value.dateTo.split(' ')[0] : null;
            if (type === 'inRange' && from && to) {
                filters.push({ [key]: from, condition: 'gte' }, { [key]: to, condition: 'lte' });
            } else if (type === 'lessThan' && from) {
                filters.push({ [key]: from, condition: 'lt' });
            } else if (type === 'greaterThan' && from) {
                filters.push({ [key]: from, condition: 'gt' });
            } else if (from) {
                filters.push({ [key]: from, condition: agGridToApiCondition[type] || 'equals' });
            }
            continue;
        }
        appendAgGridConditionsToApiFilters(filters, key, value);
    }
    return filters;
}

function parseAgNumberCountFilterText(text: string | null): number | null {
    if (text == null || text === '') return null;
    const n = Number.parseInt(text, 10);
    return Number.isNaN(n) ? null : n;
}

function formatAgNumberCountFilterValue(value: number | null): string {
    return value == null || Number.isNaN(value) ? '' : String(value);
}

function countFieldValueGetter(field: string) {
    return (params: any) => {
        const v = params.data?.[field];
        return v === undefined || v === null ? null : Number(v);
    };
}

const agentHistoryCountColBase = {
    cellStyle: countColumnCellStyle,
    filter: 'agNumberColumnFilter',
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    flex: 1,
    sortable: true,
    headerClass: 'ag-center-header',
};


function useUrlPanelSync(
    paramName: string,
    token: string | null | undefined,
    location: ReturnType<typeof useLocation>,
    navigate: ReturnType<typeof useNavigate>,
    onOpen: (id: string) => void,
    onClose: () => void,
) {
    const rawValue = new URLSearchParams(location.search).get(paramName);
    const decodedValue = rawValue
        ? (() => { try { return globalThis.atob(rawValue); } catch { return rawValue; } })()
        : null;

    const prevRef = useRef<string | null>(null);
    const wasRowClickRef = useRef(false);
    // Keep stable refs so the effect never holds stale closures
    const onOpenRef = useRef(onOpen);
    const onCloseRef = useRef(onClose);
    useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    useEffect(() => {
        if (!token) return;
        if (decodedValue && decodedValue !== prevRef.current) {
            prevRef.current = decodedValue;
            onOpenRef.current(decodedValue);
        } else if (!decodedValue && prevRef.current) {
            prevRef.current = null;
            onCloseRef.current();
        }
    }, [decodedValue, token]);

    /** Call when a row is clicked to open the panel – pushes a history entry. */
    const push = useCallback((id: string) => {
        const encoded = globalThis.btoa(id);
        if (rawValue !== encoded) {
            wasRowClickRef.current = true;
            prevRef.current = id;
            navigate(`${location.pathname}?${paramName}=${encoded}`);
        }
    }, [navigate, location.pathname, rawValue, paramName]);

    /** Call when the panel is closed – navigates back or strips the param. */
    const handleClose = useCallback(() => {
        prevRef.current = null;
        if (wasRowClickRef.current) {
            wasRowClickRef.current = false;
            navigate(-1);
        } else {
            navigate(location.pathname, { replace: true });
        }
    }, [navigate, location.pathname]);

    return { rawValue, decodedValue, push, handleClose };
}

export function AgentHistoryTable() {
    const { t } = useTranslation();
    const token = useTypedSelector((state) => state.auth.token);
    const { agentMessageState } = useSelector((state: RootState) => state.chatAdvisorSliceReducer);

    const [getAgentHisotry, { isLoading: isLoadingAgentHistory, error: agentHistoryError }] = useGetAgentHisotryMutation();
    const [getAiWorkerUsageById] = useGetAiWorkerUsageByIdMutation();
    const { data: agentListData } = useGetAiWorkersByCategoryQuery({ category: categoryType.AGENT }, { skip: !token, refetchOnMountOrArgChange: true });
    const { data: mapperListData } = useGetMappersListQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
    const [selectedAgentFilter, setSelectedAgentFilter] = useState<string>('all');

    const persistedQuery = useMemo(() => getPersistedTableQueryState(AGENT_HISTORY_TABLE_ID), []);
    const { augmentGridReady, mergeColumnDefs, ignoreRestoreQueryEventsRef } =
        useAgGridStatePersistence(AGENT_HISTORY_TABLE_ID);
    const [agGridSort, setAgGridSort] = useState<{ field: string; order: string; }>(
        () => persistedSortForApi(persistedQuery) ?? { field: '', order: '' }
    );
    const [rerunningRowId, setRerunningRowId] = useState<string | null>(null);
    const [isExecutePanelOpen, setIsExecutePanelOpen] = useState(false);
    const [selectedAgentData, setSelectedAgentData] = useState<any>(null);
    const [selectedEntityId, setSelectedEntityId] = useState<string>('');
    const [executionTimestamp, setExecutionTimestamp] = useState<number>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUsageData, setSelectedUsageData] = useState<any>(null);
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [selectedEventUsageId, setSelectedEventUsageId] = useState<string | null>(null);
    const [isVoiceAgentDetail, setIsVoiceAgentDetail] = useState(false);
    const [isCallLogDrawerOpen, setIsCallLogDrawerOpen] = useState(false);
    const [selectedCallLogId, setSelectedCallLogId] = useState<string | null>(null);
    const [isOpeningPanel, setIsOpeningPanel] = useState(false);
    const [gridApi, setGridApi] = useState<any>(null);
    // Set once persisted filters/sort are applied, so the first request already uses them
    const [isGridRestored, setIsGridRestored] = useState(false);
    // The grid holds only the page shown; it changes when the next page's response arrives
    const [rows, setRows] = useState<any[]>([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
    // Page/size of the latest request, so sort/filter reload the page being shown or requested
    const targetPageRef = useRef({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
    const requestIdRef = useRef(0);
    const inFlightRequestKeyRef = useRef<string | null>(null);
    const loadPageRef = useRef<(page: number, pageSize: number) => void>(() => undefined);
    const location = useLocation();
    const navigate = useNavigate();

    // URL-sync hooks – keep these before the effects that depend on them
    const idParam = useUrlPanelSync('id', token, location, navigate,
        (usageId) => fetchAndOpenModal(usageId),
        () => setIsModalOpen(false),
    );
    const callIdParam = useUrlPanelSync('call_Id', token, location, navigate,
        (usageId) => { setSelectedCallLogId(usageId); setIsCallLogDrawerOpen(true); },
        () => { setIsCallLogDrawerOpen(false); setSelectedCallLogId(null); },
    );
    const [agGridFilters, setAgGridFilters] = useState<any[]>(() =>
        buildAgentHistoryFilters(persistedQuery?.filterModel as Record<string, any> | undefined)
    );
    // Latest sort/filters for requests made in the same event that changes them (state lags a render)
    const queryRef = useRef({ filters: agGridFilters, sort: agGridSort });

    const allAgentOptions = useMemo(() => {
        const agents = ((agentListData as any)?.agents ?? agentListData ?? []).map((a: any) => ({ id: a.aiworker_id, name: a.name }));
        const mappers = (mapperListData ?? []).map((m: any) => ({ id: m.aiworker_id, name: m.name }));
        return [...agents, ...mappers];
    }, [agentListData, mapperListData]);

    const handleAgentDropdownChange = useCallback((agentId: string) => {
        if (agentId === selectedAgentFilter) return;
        setSelectedAgentFilter(agentId);
    }, [selectedAgentFilter]);

    // Server-side pagination: one API call per page with page/per_page matching the grid. The current page
    // (rows and footer numbers) stays on screen under the loader until the response replaces it.
    loadPageRef.current = (page: number, pageSize: number) => {
        targetPageRef.current = { page, pageSize };
        const body = buildAgentHistoryQueryPayload({
            page,
            perPage: pageSize,
            columnFilters: queryRef.current.filters,
            selectedAgentFilter,
            sort: queryRef.current.sort,
        });
        const requestKey = JSON.stringify(body);
        if (inFlightRequestKeyRef.current === requestKey) return; // same request already on its way
        inFlightRequestKeyRef.current = requestKey;
        const requestId = ++requestIdRef.current;
        getAgentHisotry(body).unwrap()
            .then((result: any) => {
                if (requestId !== requestIdRef.current) return; // replaced by a newer request
                const total = result.total ?? 0;
                // A filter can leave fewer pages than the one requested: show the new last page instead
                const lastPage = Math.max(1, Math.ceil(total / pageSize));
                if (page > lastPage) {
                    inFlightRequestKeyRef.current = null;
                    loadPageRef.current(lastPage, pageSize);
                    return;
                }
                setRows(updateFeedbackCounts(result.aiworkers_conversations ?? []));
                setPagination({ page, pageSize, total });
            })
            .catch((error: any) => {
                if (requestId !== requestIdRef.current) return;
                console.log(error);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) inFlightRequestKeyRef.current = null;
            });
    };
    const loadPage = useCallback((page: number, pageSize: number) => loadPageRef.current(page, pageSize), []);
    const reloadCurrentPage = useCallback(
        () => loadPage(targetPageRef.current.page, targetPageRef.current.pageSize),
        [loadPage]
    );

    // First load once persisted filters/sort are applied; changing the agent starts again at page 1
    useEffect(() => {
        if (!token || !isGridRestored) return;
        loadPage(1, targetPageRef.current.pageSize);
    }, [token, isGridRestored, selectedAgentFilter, loadPage]);

    useEffect(() => {
        if (!gridApi || !agentMessageState) return;
        gridApi.forEachNode((node: any) => {
            if (node.data) node.setData(updateFeedbackCounts([node.data])[0]);
        });
    }, [agentMessageState]);

    // Re-fetch table data when Agent Execute panel closes
    const prevExecuteOpenRef = useRef<boolean>(false);
    useEffect(() => {
        const wasOpen = prevExecuteOpenRef.current;
        if (wasOpen && !isExecutePanelOpen) {
            // Panel just closed: reload the current page to get latest statuses
            reloadCurrentPage();
        }
        prevExecuteOpenRef.current = isExecutePanelOpen;
    }, [isExecutePanelOpen, reloadCurrentPage]);

    const updateFeedbackCounts = (data: any[]) => {
        if (!data?.length || !agentMessageState) return data;

        return data.map((item: any) => {

            const messageId = item.message_id || item.aiworker_usage_id;
            if (!messageId) return item;

            const agentState = agentMessageState[messageId];
            if (!agentState) return item;

            const { comments = [], feedback = '' } = agentState;

            return {
                ...item,
                likes_count: feedback === 'like' ? 1 : 0,
                dislikes_count: feedback === 'dislike' ? 1 : 0,
                comments_count: comments.length
            };
        });
    }

    const onSortChanged = (params: any) => {
        if (!params.api || ignoreRestoreQueryEventsRef.current) return;

        const sortColumns = params.api.getColumnState().find((col: any) => col.sort);

        // No sorted column means the sort was cleared (third click)
        const sort = sortColumns
            ? { field: sortColumns.colId, order: sortColumns.sort as 'asc' | 'desc' }
            : { field: '', order: '' };
        queryRef.current.sort = sort;
        setAgGridSort(sort);
        reloadCurrentPage();
    };


    const handleRerunAgent = (rowData: any) => {
        const rowId = rowData?.id || rowData?.usage_id || rowData?.aiworker_usage_id;
        if (!rowId || rerunningRowId !== null) return;

        try {
            setRerunningRowId(rowId);

            const entityId = rowData?.entity_value?.[0] || rowData?.entity_id || rowData?.input_data?.entity_id || rowData?.tag_values?.join(',');
            const agentName = rowData?.input_data?.aiworker_name || rowData?.aiworker_name || rowData?.agent_name;

            if (!rowData?.aiworker_id) {
                throw new Error('Agent information not found.');
            }

            if (!entityId) {
                throw new Error('Entity ID not found.');
            }

            const fullAgent = ((agentListData as any)?.agents ?? agentListData ?? []).find((a: any) => a.aiworker_id === rowData.aiworker_id);
            const agentData = {
                aiworker_id: rowData.aiworker_id,
                name: agentName,
                id: rowData.aiworker_id,
                ...(fullAgent?.aiworkers_attrs ? { aiworkers_attrs: fullAgent.aiworkers_attrs } : {}),
            };

            const timestamp = Date.now();
            startTransition(() => {
                setSelectedAgentData(agentData);
                setSelectedEntityId(entityId);
                setExecutionTimestamp(timestamp);
                setIsExecutePanelOpen(true);
            });

        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message ?? 'Failed to open agent execute panel');
        } finally {
            setRerunningRowId(null);
        }
    }

    const handleModalOpenChange = useCallback((open: boolean) => {
        setIsModalOpen(open);
        if (!open) {
            idParam.handleClose();
        }
    }, [idParam]);

    const handleButtonAction = (_action: string, _buttonData: any) => {
        handleModalOpenChange(false);
    };

    const fetchAndOpenModal = async (usageId: string, isVoiceAgent?: boolean, rowData?: any) => {
        setIsOpeningPanel(true);
        try {
            const result = await getAiWorkerUsageById(usageId).unwrap();
            const raw = isVoiceAgent && result?.data !== undefined ? result.data : result;
            const usageData = Array.isArray(raw) ? [...raw] : [{ ...raw }];
            if (rowData && usageData[0]) {
                usageData[0] = {
                    ...usageData[0],
                    message_id: rowData.message_id || usageData[0].message_id,
                };
            }
            setSelectedUsageData(usageData);
            setIsVoiceAgentDetail(Boolean(isVoiceAgent));
            setIsModalOpen(true);
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message ?? 'Failed to load agent details. Please try again.');
        } finally {
            setIsOpeningPanel(false);
        }
    };

    const handleRefresh = useCallback(async () => {
        if (!selectedUsageData) return;
        const item = Array.isArray(selectedUsageData) ? selectedUsageData[0] : selectedUsageData;
        const id = item?.id ?? item?.aiworker_usage_id ?? item?.usage_id ?? null;
        if (!id) return;
        try {
            const result = await getAiWorkerUsageById(id).unwrap();
            const raw = isVoiceAgentDetail && result?.data !== undefined ? result.data : result;
            const usageData = Array.isArray(raw) ? [...raw] : [{ ...raw }];
            if (item?.message_id && usageData[0]) {
                usageData[0] = {
                    ...usageData[0],
                    message_id: item.message_id || usageData[0].message_id,
                };
            }
            setSelectedUsageData(usageData);
            reloadCurrentPage();
            CtyShowToast(checkType.success, t('Refreshed successfully'));
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message ?? 'Failed to refresh. Please try again.');
        }
    }, [selectedUsageData, isVoiceAgentDetail, getAiWorkerUsageById, reloadCurrentPage, t]);

    const handleRowClick = (event: any) => {
        if (isOpeningPanel) return;

        // Ignore clicks originating from rerun cell/button or events cell
        const nativeEvent: MouseEvent | undefined = event?.event;
        if (nativeEvent) {
            const target = nativeEvent.target as HTMLElement | null;
            if (target && (target.closest('[data-no-row-click="true"]') || target.closest('.rerun-cell') || target.closest('.aiworkers-usage-event-cell'))) {
                return;
            }
        }

        const rowData = event.data;
        const aiworkerUsageId = rowData.id || rowData.usage_id || rowData.aiworker_usage_id;
        const isVoiceAgent = rowData?.type === 'Voice Agent';

        // When sub_type is voice_agent: open CallLogDrawer (voice call log detail)
        if (isVoiceAgent && aiworkerUsageId) {
            callIdParam.push(aiworkerUsageId);
            setSelectedCallLogId(aiworkerUsageId);
            setIsCallLogDrawerOpen(true);
            return;
        }

        // Original row click behavior - show claim details modal
        if (!aiworkerUsageId) {
            setSelectedUsageData(rowData);
            setIsVoiceAgentDetail(false);
            setIsModalOpen(true);
            return;
        }
        idParam.push(aiworkerUsageId);
        void fetchAndOpenModal(aiworkerUsageId, false, rowData);
    }

    const onFilterChanged = (params: any) => {
        if (ignoreRestoreQueryEventsRef.current) return;
        const filters = buildAgentHistoryFilters(params.api.getFilterModel());
        queryRef.current.filters = filters;
        setAgGridFilters(filters);
        reloadCurrentPage();
    }

    const resetFilters = useCallback(() => {
        if (!gridApi) return;

        const filterModel = gridApi.getFilterModel();
        const hasFilters = filterModel && Object.keys(filterModel).length > 0;
        const hasSorts = gridApi.getColumnState?.()?.some((c: any) => c.sort);

        if (!hasFilters && !hasSorts && agGridFilters.length === 0) return;

        queryRef.current = { filters: [], sort: { field: '', order: '' } };
        setAgGridSort(queryRef.current.sort);
        setAgGridFilters([]);

        // One reload below instead of one per cleared filter/sort event
        ignoreRestoreQueryEventsRef.current = true;
        const allColumns = gridApi.getAllGridColumns?.() || [];
        allColumns.forEach((col: any) => {
            const colId = col.getColId?.();
            if (colId) gridApi.destroyFilter(colId);
        });
        gridApi.setFilterModel(null);
        const colState = gridApi.getColumnState?.();
        if (colState) {
            gridApi.applyColumnState?.({ state: colState.map((c: any) => ({ ...c, sort: null, sortIndex: null })) });
        }
        ignoreRestoreQueryEventsRef.current = false;
        loadPage(1, targetPageRef.current.pageSize);
    }, [gridApi, agGridFilters, loadPage]);

    const textFilterParams = {
        caseSensitive: false,
        debounceMs: 1000,
        readOnly: isLoadingAgentHistory,
        buttons: ['reset', 'apply'],
        maxNumConditions: 5,
        closeOnApply: true,
    };

    const numberCountFilterParams = {
        allowedCharPattern: '\\d',
        numberParser: parseAgNumberCountFilterText,
        numberFormatter: formatAgNumberCountFilterValue,
        filterOptions: ['equals'],
        defaultOption: 'equals',
        debounceMs: 1000,
        readOnly: isLoadingAgentHistory,
        buttons: ['reset', 'apply'],
        maxNumConditions: 5,
        closeOnApply: true,
    };

    const columnDefs = useMemo(() => [
        {
            headerName: `${t('Agent Name')}`,
            field: "aiworker_name",
            sortable: true,
            filter: true,
            filterParams: textFilterParams,
            floatingFilter: true,
            suppressFloatingFilterButton: true,
            valueGetter: (params: any) => params.data?.input_data?.aiworker_name || params.data?.aiworker_name || '',
            flex: 1,
            minWidth: 180,
            resizable: true,
            suppressSizeToFit: false,
            suppressAutoSize: true,
        },
        {
            headerName: `${t('Entity ID')}`,
            field: "tag_values",
            sortable: true,
            filter: true,
            floatingFilter: true,
            filterParams: textFilterParams,
            valueGetter: (params: any) => params.data?.tag_values.join(','),
            cellRenderer: EntityIdCellRenderer,
            cellRendererParams: {
                onViewTemplate: (aiworkerId: string, entityId: string, aiworkerUsageId: string) => {
                    window.open(`/agents/history/${encodeURIComponent(aiworkerId)}/${encodeURIComponent(entityId)}/${encodeURIComponent(aiworkerUsageId)}`, '_blank', 'noopener,noreferrer');
                }
            },
            suppressFloatingFilterButton: true,
            flex: 1,
            minWidth: 140,
            resizable: true,
            suppressSizeToFit: false,
            suppressAutoSize: true,
            cellClass: 'text-center',
            headerClass: 'ag-center-header',
        },
        {
            headerName: `${t('Product Type')}`,
            field: 'metadata.policy_coverage_code',
            sortable: true,
            filter: true,
            floatingFilter: true,
            filterParams: textFilterParams,
            suppressFloatingFilterButton: true,
            valueGetter: (params: any) => params.data?.metadata?.policy_coverage_code ?? '-',
            flex: 1,
            minWidth: 140,
            resizable: true,
            suppressSizeToFit: false,
            suppressAutoSize: true,
            cellClass: 'text-center',
        },
        {
            headerName: `${t('Executed By')}`,
            field: 'user_name',
            sortable: true,
            filter: true,
            floatingFilter: true,
            filterParams: textFilterParams,
            suppressFloatingFilterButton: true,
            valueGetter: (params: any) => params.data?.user_name ?? '',
            flex: 1,
            minWidth: 140,
            resizable: true,
            suppressSizeToFit: false,
            suppressAutoSize: true,
        },

        {
            headerName: `${t('Status')}`,
            field: 'status',
            sortable: true,
            filter: true,
            floatingFilter: true,
            filterParams: textFilterParams,
            suppressFloatingFilterButton: true,
            valueGetter: (params: any) => {
                const status = params.data?.status ?? '';
                return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
            },
            flex: 1,
            minWidth: 120,
            resizable: true,
            suppressSizeToFit: false,
            suppressAutoSize: true,
            cellClass: 'text-center',
            headerClass: 'ag-center-header', 
        },
        {
            headerName: 'State',
            field: 'state',
            sortable: true,
            filter: true,
            floatingFilter: true,
            filterParams: textFilterParams,
            valueGetter: (params: any) => params.data?.state?.replaceAll('_', ' ') ?? '-',
            headerClass: 'ag-center-header', 
            cellClass: 'capitalize',
        },
        {
            headerName: `${t('Timestamp')}`,
            field: "date",
            sortable: true,
            filter: CustomDateFilter,
            floatingFilter: true,
            suppressFloatingFilterButton: true,
            floatingFilterComponent: DateFilterComponent,
            floatingFilterComponentParams: {
                disabled: isLoadingAgentHistory
            },
            valueGetter: (params: any) => params?.data?.date || null,
            valueFormatter: (params: any) => params.value ? formatDateTime12Hour(params.value) : '',
            flex: 1,
            minWidth: 200,
            resizable: true,
            suppressSizeToFit: false,
            suppressAutoSize: true,
        },
        {
            headerName: `${t('Title')}`,
            field: "title",
            sortable: true,
            filter: true,
            floatingFilter: true,
            filterParams: textFilterParams,
            suppressFloatingFilterButton: true,
            flex: 1,
            minWidth: 120,
            resizable: true,
            suppressSizeToFit: false,
            suppressAutoSize: true,
            cellClass: 'text-center',
            headerClass: 'ag-center-header', 
            valueGetter: (params: any) => {
                const title = params.data?.title || '';
                const truncatedTitle = title.length > 50 ? title.substring(0, 50) + '...' : title;
                return truncatedTitle;
            }
        },
        {
            headerName: `${t('Agent Score')}`,
            field: 'agent_score',
            valueGetter: (params: any) => params.data?.agent_score || params.data?.agent_score === 0 ? `${Math.round(parseScore(params.data?.agent_score))}%` : '',
            cellStyle: { alignItems: "center", display: "flex", justifyContent: "center" },
            minWidth: 120,
            filter: false,
            flex: 1,
            sortable: true,
            headerClass: 'ag-center-header', 
        },
        {
            ...agentHistoryCountColBase,
            headerName: `${t('Likes')}`,
            field: 'likes_count',
            valueGetter: countFieldValueGetter('likes_count'),
            minWidth: 100,
            filterParams: numberCountFilterParams,
        },
        {
            ...agentHistoryCountColBase,
            headerName: `${t('Dislikes')}`,
            field: 'dislikes_count',
            valueGetter: countFieldValueGetter('dislikes_count'),
            minWidth: 100,
            filterParams: numberCountFilterParams,
        },
        {
            ...agentHistoryCountColBase,
            headerName: `${t('Comments')}`,
            field: 'comments_count',
            valueGetter: countFieldValueGetter('comments_count'),
            minWidth: 120,
            filterParams: numberCountFilterParams,
        },
        {
            headerName: `${t('Rerun')}`,
            field: 'rerun',
            cellRenderer: RerunCellRenderer,
            cellRendererParams: {
                rerunningRowId,
                isAnyRerunInProgress: rerunningRowId !== null,
                onRerun: handleRerunAgent
            },
            cellStyle: { alignItems: "center", display: "flex", justifyContent: "center" },
            minWidth: 100,
            filter: false,
            flex: 1,
            sortable: false,
            suppressMenu: true,
            headerClass: 'ag-center-header', 
        },
        {
            headerName: `${t('AI Workers Usage')}`,
            field: 'aiworkers_usage_event',
            cellRenderer: AiworkersUsageEventCellRenderer,
            cellRendererParams: {
                onCellClick: (aiworkerUsageId: string) => {
                    setSelectedEventUsageId(aiworkerUsageId);
                    setIsEventModalOpen(true);
                }
            },
            cellStyle: { alignItems: "center", display: "flex", justifyContent: "center", cursor: "pointer", padding: 0, margin: 0},
            minWidth: 150,
            filter: false,
            flex: 1,
            sortable: false,
            suppressMenu: true,
            suppressCellSelection: true,
            headerClass: 'ag-center-header',
        }
    ].map((col) => (
      col.floatingFilter && !col.floatingFilterComponent
        ? { ...col, floatingFilterComponent: DebouncedTextFloatingFilter }
        : col
    )), [t, isLoadingAgentHistory, rerunningRowId, handleRerunAgent]);

    const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

    const onGridReady = useCallback((params: any) => {
        augmentGridReady(params, (p) => {
            setGridApi(p.api);
            p.api.sizeColumnsToFit();
            // Runs after the persisted filters/sort are restored (also scheduled on grid ready)
            setTimeout(() => setIsGridRestored(true), 0);
        });
    }, [augmentGridReady]);


    const tableProps = {
        error: agentHistoryError,
        isLoading: false, // the grid stays mounted; the loader covers the current page while the next loads
        inlineLoading: isLoadingAgentHistory,
        tableData: rows,
        serverPagination: {
            ...pagination,
            onPageChange: (page: number) => loadPage(page, targetPageRef.current.pageSize),
            // Keep the first row shown on screen when the page size changes
            onPageSizeChange: (pageSize: number) =>
                loadPage(Math.floor(((pagination.page - 1) * pagination.pageSize) / pageSize) + 1, pageSize),
        },
        tableColumnDefs: columnDefsWithSort,
        onRowClicked: handleRowClick,
        onSortChanged: onSortChanged,
        onGridReady: onGridReady,
        onFilterChanged: onFilterChanged,
        disableRowClick: rerunningRowId !== null,
        useAutoHeight: false,
        containerStyle: { height: '100%', minHeight: 0 }
    }

    return (
        <div className="relative flex flex-col h-full min-h-0">
            {isOpeningPanel && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/70">
                    <CtySpinner size="md" />
                </div>
            )}
            <h1 className="text-xl font-semibold mb-3">{t('Agent History')}</h1>
            <div className="flex flex-wrap gap-2 mb-3 items-center justify-between w-full">
                <div className="flex flex-wrap gap-2 items-center">
                <CtyButton id="agent-history-reset-filters-btn" className="rounded-md p-3 border-1" variant="bordered" size="sm" onPress={resetFilters}>
                    {t('Reset Filters')}
                </CtyButton>
                <CtyButton id="agent-history-refresh-btn" className="rounded-md p-3 border-1" variant="bordered" size="sm" onPress={reloadCurrentPage}>
                    <span className="inline-flex items-center gap-1.5">
                        <Icon icon="heroicons:arrow-path" width={16} height={16} />
                        {t('Refresh')}
                    </span>
                </CtyButton>
                </div>
                <div className="inline-flex shrink-0 items-center gap-2">
                <UsageQueryExportButton
                    id="agent-history-export-btn"
                    fileBaseName="agent-history"
                    placement="bottom-end"
                    getQueryPayload={() => buildAgentHistoryQueryPayload({
                        page: 1,
                        perPage: pagination.pageSize,
                        columnFilters: agGridFilters,
                        selectedAgentFilter,
                        sort: agGridSort,
                    })}
                />
                {allAgentOptions.length > 0 && (
                    <div className="w-96 shrink-0">
                    <Select
                        label=""
                        placeholder={t('Select an Agent')}
                        className="max-w-sm"
                        disallowEmptySelection
                        selectedKeys={new Set([selectedAgentFilter])}
                        onSelectionChange={(keys) => {
                            const agentId = String([...keys][0] ?? 'all');
                            handleAgentDropdownChange(agentId);
                        }}
                        aria-label="agent-filter-selection"
                        id="agent-history-agent-selection"
                        size="sm"
                    >
                        <>
                            <SelectItem key="all" value="all">{t('All Agents')}</SelectItem>
                            {allAgentOptions.map((agent: any) => (
                                <SelectItem key={agent.id} value={agent.id}>
                                    {agent.name}
                                </SelectItem>
                            ))}
                        </>
                    </Select>
                    </div>
                )}
                </div>
            </div>

            <div className="flex-1 min-h-0">
                <ErrorBoundAgGridTable {...tableProps} />
            </div>

            <AgentExecutionDataRenderer
                isOpen={isModalOpen}
                onOpenChange={handleModalOpenChange}
                claimData={selectedUsageData}
                onButtonAction={handleButtonAction}
                onRefresh={handleRefresh}
                isLoading={false}
            />

            <AgentExecutePanel
                isOpen={isExecutePanelOpen}
                onOpenChange={setIsExecutePanelOpen}
                agentData={selectedAgentData}
                entityId={selectedEntityId}
                autoExecute={true}
                isLoading={false}
                executionTimestamp={executionTimestamp}
            />

            <AiworkersUsageEventModal
                isOpen={isEventModalOpen}
                onClose={() => {
                    setIsEventModalOpen(false);
                    setSelectedEventUsageId(null);
                }}
                aiworkerUsageId={selectedEventUsageId}
            />

            <CallLogDrawer
                isOpen={isCallLogDrawerOpen}
                onClose={() => {
                    setIsCallLogDrawerOpen(false);
                    setSelectedCallLogId(null);
                    callIdParam.handleClose();
                }}
                callLogId={selectedCallLogId}
            />
        </div>
    );

}

// External cell renderer for AI Workers Usage Event column
const AiworkersUsageEventCellRenderer = (props: any) => {
    const { data, onCellClick } = props;
    const { t } = useTranslation();

    const aiworkerUsageId = data?.id || data?.usage_id || data?.aiworker_usage_id;

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (aiworkerUsageId && onCellClick) {
            onCellClick(aiworkerUsageId);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e);
        }
    };

    if (!aiworkerUsageId) {
        return <div className="text-default-400">-</div>;
    }

    return (
        <button
            type="button"
            className="aiworkers-usage-event-cell flex items-center justify-center gap-2 px-2 py-1 hover:bg-default-100 rounded transition-colors w-full h-full cursor-pointer border-0 bg-transparent text-left"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onMouseDown={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
            }}
            onMouseUp={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
            }}
            onDoubleClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
            }}
            data-no-row-click="true"
            style={{ width: '100%', height: '100%', minHeight: '100%' }}
        >
            <span className="text-sm text-default-600">{t('View Events')}</span>
        </button>
    );
};

// External cell renderer for Entity ID column
const EntityIdCellRenderer = (props: any) => {
    const { data, onViewTemplate } = props;
    const { t } = useTranslation();

    const aiworkerId = data?.aiworker_id;
    const aiworkerUsageId = data?.id || data?.usage_id || data?.aiworker_usage_id;
    const entityIdDisplay = Array.isArray(data?.tag_values) ? data.tag_values.join(',') : '';
    const entityId = (Array.isArray(data?.tag_values) && data.tag_values.length > 0 ? data.tag_values[0] : null)
        || data?.entity_number
        || aiworkerUsageId;
    const agentName = data?.aiworker_name || data?.input_data?.aiworker_name || '';
    const isClaimIntakeAgent = agentName.toLowerCase().includes('claim intake');
    const hasClaimNumber = /^CL/i.test(String(entityIdDisplay).trim());
    const showViewDetailsEye = Boolean(
        aiworkerId && aiworkerUsageId && entityIdDisplay && !(isClaimIntakeAgent && !hasClaimNumber)
    );

    const handleViewTemplateClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (aiworkerId && aiworkerUsageId && onViewTemplate) {
            onViewTemplate(aiworkerId, entityId, aiworkerUsageId);
        }
    };

    const handleViewTemplateKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            handleViewTemplateClick(e);
        }
    };

    return (
        // Note: no data-no-row-click here — clicking the entity id text itself
        // should still trigger the existing row-click behavior. Only the view
        // icon button below opts out of it.
        <div className="flex items-center justify-center gap-1 w-full h-full">
            <span className="truncate">{entityIdDisplay}</span>
            {showViewDetailsEye && (
                <Tooltip content={t('View Details')}>
                    <button
                        type="button"
                        aria-label={t('View Details')}
                        className="flex items-center justify-center p-1 hover:bg-default-100 rounded transition-colors cursor-pointer border-0 bg-transparent"
                        onClick={handleViewTemplateClick}
                        onKeyDown={handleViewTemplateKeyDown}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                        }}
                        onDoubleClick={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                        }}
                        data-no-row-click="true"
                    >
                        <Icon icon="mdi:eye-outline" width={16} className="text-default-600" />
                    </button>
                </Tooltip>
            )}
        </div>
    );
};

// External cell renderer for rerun column (to satisfy Sonar "component in parent" rule)
const RerunCellRenderer = (props: any) => {
    const { data, rerunningRowId, isAnyRerunInProgress, onRerun } = props;

    const rowId = data?.id || data?.usage_id || data?.aiworker_usage_id;
    const isRerunning = rerunningRowId === rowId;
    const status = (data?.status || '').toLowerCase();
    const isInProgress = status === 'in_progress';
    const isDisabled = isRerunning || isAnyRerunInProgress || isInProgress;

    const handleRerunClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (isDisabled) return;
        onRerun(data);
    };

    return (
        <div
            className="flex items-center justify-center rerun-cell"
            data-no-row-click="true"
            onMouseDown={(e) => { e.stopPropagation(); }}
            onDoubleClick={(e) => { e.stopPropagation(); }}
        >
            <button
                onClick={handleRerunClick}
                onMouseDown={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                disabled={isDisabled}
                className="p-1 hover:bg-default-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                title={isInProgress ? 'Agent is already in progress' : 'Rerun Agent'}
                data-no-row-click="true"
            >
                {isRerunning ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                ) : (
                    <Icon icon="mdi:refresh" className="w-4 h-4 text-default-500 hover:text-primary" />
                )}
            </button>
        </div>
    );
};